import { ArrowForwardRounded, AttachMoneyRounded, LocationOnOutlined } from "@mui/icons-material";
import { Button, Card, CardContent, Chip, Divider, Stack, Typography } from "@mui/material";
import { Link } from "react-router-dom";
import type { PublicOffer } from "./types";
import { useLanguage } from "../../../../contexts/LanguageContext";
import translations from "./OfferCard.translations.json";

type SupportedLanguage = keyof typeof translations;

interface OfferCardProps {
  offer: PublicOffer;
}

export const OfferCard = ({ offer }: OfferCardProps) => {
  const { language } = useLanguage();
  const content = translations[language as SupportedLanguage] || translations.en;

  return (
  <Card
    elevation={0}
    sx={{
      width: "100%",
      height: "100%",
      display: "flex",
      flexDirection: "column",
      borderRadius: "16px",
      bgcolor: "#e0f2fe",
      border: "1px solid #bae6fd",
      p: 1,
      boxShadow: "0 4px 12px rgba(14, 116, 144, 0.08)",
    }}
  >
    <CardContent sx={{ display: "flex", flexDirection: "column", gap: 2, flex: 1 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={2}>
        <Stack spacing={0.5} minWidth={0}>
          <Typography variant="h6" component="h2" sx={{ fontWeight: 700, fontSize: "1.1rem", lineHeight: 1.25, color: "#0369a1" }} noWrap>
            {offer.title || content.untitled}
          </Typography>
          <Typography variant="body2" sx={{ color: "#334155", mt: 0.5, fontWeight: 600 }} noWrap>
            {offer.company || content.company}
          </Typography>
        </Stack>
        <Chip label={offer.jobType || content.any} size="small" sx={{ bgcolor: "#dcfce7", color: "#14532d", fontWeight: 700, borderRadius: "8px" }} />
      </Stack>

      <Typography variant="body2" sx={{ color: "#1e293b", lineHeight: 1.6 }}>
        {offer.description || content.description}
      </Typography>

      {offer.tag?.length > 0 && (
        <Stack direction="row" flexWrap="wrap" useFlexGap gap={0.75}>
          {offer.tag?.map((item) => <Chip key={item?.tag} label={item?.tag} size="small" sx={{ bgcolor: "#ffffff", color: "#0f172a", border: "1px solid #bae6fd", borderRadius: "6px", fontSize: "0.75rem", fontWeight: 600 }} />)}
        </Stack>
      )}

      <Divider sx={{ mt: "auto" }} />
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ color: "#334155", pt: 0.5 }}>
        <Stack direction="row" spacing={0.75} alignItems="center">
          {(offer.location || offer.workPlace) && <LocationOnOutlined sx={{ fontSize: 18 }} />}
          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.85rem", color: "#334155" }}>
            {offer.location?.label}{offer.workPlace ? ` • ${offer.workPlace}` : ""}
          </Typography>
        </Stack>
        {offer.salary?.label && <Stack direction="row" spacing={0.75} alignItems="center"><AttachMoneyRounded sx={{ fontSize: 18 }} /><Typography variant="body2" sx={{ fontWeight: 700, fontSize: "0.85rem", color: "#0f172a" }}>{offer.salary?.label}</Typography></Stack>}
      </Stack>

      <Button component={Link} to={offer.cta?.url || "#"} variant="contained" endIcon={<ArrowForwardRounded />} sx={{ alignSelf: "flex-end", mt: "auto", bgcolor: "#007a5a", borderRadius: "8px", textTransform: "none", fontWeight: 600, px: 3, py: 0.8, "&:hover": { bgcolor: "#006147" } }}>
        {offer.cta?.label || content.apply}
      </Button>
    </CardContent>
  </Card>
  );
};
